import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./MonthlySaleReportPage.css";
import "./YearlySaleReportPage.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { startLoading, stopLoading, setTaskProgress, LOADING_TYPES, SkeletonTable, SkeletonChart } from "../loading";
import {
  buildBrandMatrixCsv,
  buildBrandYearlyMatrix,
  buildYearlyGridCsv,
  computeYearlyTotals,
  loadYearlySaleReportRows,
  loadYearlySaleReportRowsWithProgress,
  pctYoY,
} from "../services/yearlySaleReportService";
import {
  formatFinancialYearLabel,
  readStoredFinancialYearStartYear,
  getFinancialYearDropdownOptions,
  FY_MONTH_ORDER_SHORT,
} from "../utils/financialYear";

const BRANCH_OPTIONS = [
  { value: "Main", label: "Main Branch" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
];

const GRID_COLUMNS = [
  { key: "totalSale", label: "Total Sale" },
  { key: "totalPurchase", label: "Total Purchase" },
  { key: "cashSale", label: "Cash Sale" },
  { key: "creditBill", label: "Credit Bill" },
  { key: "otherSale", label: "Other Sale" },
  { key: "saleCollection", label: "Sale Collection" },
  { key: "subtotal", label: "Subtotal" },
  { key: "miscSale", label: "Misc Sale" },
  { key: "miscPurchase", label: "Misc Purchase" },
  { key: "transferOutCurr", label: "Br. Transfer Out" },
  { key: "totalCashInHand", label: "Cash in Hand" },
];

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const s = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
  return n < 0 ? `(${s})` : s;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const s = n.toFixed(1);
  return `${n >= 0 ? "+" : ""}${s}%`;
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function YearlySaleReportPage() {
  /** FY start calendar year (April of this year → March of next). */
  const [year, setYear] = useState(() => readStoredFinancialYearStartYear());
  const [branchFilter, setBranchFilter] = useState("Main");
  const [rows, setRows] = useState([]);
  const [priorRows, setPriorRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareLoading, setCompareLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 12 });
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [comparePriorYear, setComparePriorYear] = useState(false);
  const [useFastLoad, setUseFastLoad] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() =>
    Object.fromEntries(GRID_COLUMNS.map((c) => [c.key, true]))
  );

  const loadMain = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    setPriorRows(null);
    setLoadProgress({ loaded: 0, total: 12 });
    const taskId = startLoading({
      type: LOADING_TYPES.REPORT,
      report: true,
      message: "Loading yearly sale report…",
      total: 12,
    });
    try {
      if (useFastLoad) {
        setLoadProgress({ loaded: 0, total: 12 });
        setTaskProgress(taskId, 0, 12, "Loading yearly sale report…", "months");
        const list = await loadYearlySaleReportRows(year, branchFilter);
        setRows(list);
        setLoadProgress({ loaded: 12, total: 12 });
        setTaskProgress(taskId, 12, 12, "Yearly report ready", "months");
      } else {
        await loadYearlySaleReportRowsWithProgress(year, branchFilter, ({ loaded, total, rows: r }) => {
          setLoadProgress({ loaded, total });
          setRows(r);
          setTaskProgress(taskId, loaded, total || 12, "Loading monthly data…", "months");
        });
      }
    } catch (e) {
      setError(e?.message || String(e));
      setRows([]);
    } finally {
      stopLoading(taskId);
      setLoading(false);
    }
  }, [year, branchFilter, useFastLoad]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  useEffect(() => {
    if (!comparePriorYear) {
      setPriorRows(null);
      return;
    }
    if (loading) return;
    let cancelled = false;
    setCompareLoading(true);
    (async () => {
      try {
        const prev = await loadYearlySaleReportRows(year - 1, branchFilter);
        if (!cancelled) setPriorRows(prev);
      } catch {
        if (!cancelled) setPriorRows(null);
      } finally {
        if (!cancelled) setCompareLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [comparePriorYear, year, branchFilter, loading]);

  const totals = useMemo(() => computeYearlyTotals(rows), [rows]);
  const priorTotals = useMemo(
    () => (priorRows?.length ? computeYearlyTotals(priorRows) : null),
    [priorRows]
  );
  const brandMatrix = useMemo(() => buildBrandYearlyMatrix(rows), [rows]);

  const branchLabel = BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label || branchFilter;
  const fyLabel = formatFinancialYearLabel(year);
  const title = `YEARLY REPORT - ${fyLabel} - ${branchLabel}`;

  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(year), [year]);

  const chartTrend = useMemo(
    () =>
      rows.map(({ monthLabel, data }) => ({
        month: monthLabel,
        totalSale: Number(data?.totalSale) || 0,
        totalPurchase: Number(data?.totalPurchase) || 0,
        cash: Number(data?.cashSale) || 0,
        credit: Number(data?.creditBill) || 0,
        other: Number(data?.otherSale) || 0,
      })),
    [rows]
  );

  const avgMonthlySale = rows.length ? (Number(totals.totalSale) || 0) / 12 : 0;
  const saleMix = totals.totalSale
    ? {
        cash: ((Number(totals.cashSale) || 0) / totals.totalSale) * 100,
        credit: ((Number(totals.creditBill) || 0) / totals.totalSale) * 100,
        other: ((Number(totals.otherSale) || 0) / totals.totalSale) * 100,
      }
    : { cash: 0, credit: 0, other: 0 };

  const toggleCol = (key) => {
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const exportGridCsv = () => {
    const csv = buildYearlyGridCsv(rows, totals, year, branchLabel);
    const slug = fyLabel.replace(/\s+/g, "");
    downloadText(`yearly-sale-report-FY-${slug}-${branchFilter.replace(/\s+/g, "")}.csv`, csv);
  };

  const exportBrandCsv = () => {
    const csv = buildBrandMatrixCsv(brandMatrix, year, branchLabel);
    const slug = fyLabel.replace(/\s+/g, "");
    downloadText(`yearly-brands-FY-${slug}-${branchFilter.replace(/\s+/g, "")}.csv`, csv);
  };

  const visibleGridCols = GRID_COLUMNS.filter((c) => visibleCols[c.key]);

  return (
    <>
      <div className="msr-no-print">
        <MenuBar />
        <HeaderBar />
      </div>

      <div className="msr-layout">
        <section className="msr-report-col yr-report-main">
          {loading && (
            <div className="yr-pad msr-no-print">
              <div className="yr-progress mb-4">
                <div className="yr-progress-bar">
                  <div
                    className="yr-progress-fill"
                    style={{ width: `${(loadProgress.loaded / loadProgress.total) * 100}%` }}
                  />
                </div>
                <p className="yr-progress-text">
                  Loading {loadProgress.loaded}/{loadProgress.total} months…
                </p>
              </div>
              <SkeletonChart count={2} />
              <div className="mt-4">
                <SkeletonTable rows={10} columns={6} />
              </div>
            </div>
          )}
          {error ? (
            <p className="msr-no-print msr-toolbar-error yr-pad">{error}</p>
          ) : null}

          {!loading && !error ? (
            <>
              <div className="yr-tabs msr-no-print">
                {[
                  ["overview", "Overview"],
                  ["grid", "Data grid"],
                  ["brands", "Brand matrix"],
                  ["charts", "Charts"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`yr-tab ${activeTab === id ? "yr-tab--active" : ""}`}
                    onClick={() => setActiveTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {activeTab === "overview" && (
                <div className="yr-overview">
                  <h2 className="yr-section-title">{title}</h2>
                  <div className="yr-kpi-grid">
                    <div className="yr-kpi">
                      <div className="yr-kpi-label">YTD total sale</div>
                      <div className="yr-kpi-value">{fmt(totals.totalSale)}</div>
                      {comparePriorYear && priorTotals ? (
                        <div className="yr-kpi-sub">
                          {formatFinancialYearLabel(year - 1)}: {fmt(priorTotals.totalSale)} · YoY{" "}
                          {fmtPct(pctYoY(totals.totalSale, priorTotals.totalSale))}
                        </div>
                      ) : null}
                    </div>
                    <div className="yr-kpi">
                      <div className="yr-kpi-label">YTD total purchase (net)</div>
                      <div className="yr-kpi-value">{fmt(totals.totalPurchase)}</div>
                      {comparePriorYear && priorTotals ? (
                        <div className="yr-kpi-sub">
                          {formatFinancialYearLabel(year - 1)}: {fmt(priorTotals.totalPurchase)} · YoY{" "}
                          {fmtPct(pctYoY(totals.totalPurchase, priorTotals.totalPurchase))}
                        </div>
                      ) : null}
                    </div>
                    <div className="yr-kpi">
                      <div className="yr-kpi-label">Avg monthly sale</div>
                      <div className="yr-kpi-value">{fmt(avgMonthlySale)}</div>
                      <div className="yr-kpi-sub">Total sale ÷ 12</div>
                    </div>
                    <div className="yr-kpi">
                      <div className="yr-kpi-label">YTD cash in hand (sum)</div>
                      <div className="yr-kpi-value">{fmt(totals.totalCashInHand)}</div>
                    </div>
                  </div>
                  <div className="yr-kpi-grid yr-kpi-grid--second">
                    <div className="yr-kpi yr-kpi--wide">
                      <div className="yr-kpi-label">Sale mix (YTD)</div>
                      <div className="yr-mix">
                        <span>Cash {saleMix.cash.toFixed(1)}%</span>
                        <span>Credit {saleMix.credit.toFixed(1)}%</span>
                        <span>Other {saleMix.other.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="yr-kpi">
                      <div className="yr-kpi-label">YTD branch transfer out</div>
                      <div className="yr-kpi-value">{fmt(totals.transferOutCurr)}</div>
                    </div>
                    <div className="yr-kpi">
                      <div className="yr-kpi-label">YTD sale return (less)</div>
                      <div className="yr-kpi-value">{fmt(totals.lessSaleReturn)}</div>
                    </div>
                  </div>
                  <p className="yr-note msr-no-print">
                    Financial year April–March. Same rules as Monthly Sale Report (manual lines,
                    overrides, purchase net of branch transfer out). Use “Compare prior FY” for YoY
                    KPIs vs the previous financial year.
                  </p>
                </div>
              )}

              {activeTab === "grid" && (
                <div className="yr-wrap">
                  <table className="yr-table yr-table--sticky">
                    <thead>
                      <tr>
                        <th className="yr-title" colSpan={visibleGridCols.length + 1}>
                          {title}
                        </th>
                      </tr>
                      <tr>
                        <th>Month</th>
                        {visibleGridCols.map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ calendarYear, monthIndex, monthLabel, data }) => (
                        <tr key={`${calendarYear}-${monthIndex}`}>
                          <td>{monthLabel}</td>
                          {visibleGridCols.map((c) => (
                            <td key={c.key}>{fmt(data?.[c.key])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>YTD</td>
                        {visibleGridCols.map((c) => (
                          <td key={c.key}>{fmt(totals[c.key])}</td>
                        ))}
                      </tr>
                      {comparePriorYear && priorTotals ? (
                        <tr className="yr-prior-row">
                          <td>YTD {formatFinancialYearLabel(year - 1)}</td>
                          {visibleGridCols.map((c) => (
                            <td key={c.key}>{fmt(priorTotals[c.key])}</td>
                          ))}
                        </tr>
                      ) : null}
                      {comparePriorYear && priorTotals ? (
                        <tr className="yr-yoy-row">
                          <td>YoY %</td>
                          {visibleGridCols.map((c) => (
                            <td key={c.key}>
                              {fmtPct(pctYoY(totals[c.key], priorTotals[c.key]))}
                            </td>
                          ))}
                        </tr>
                      ) : null}
                    </tfoot>
                  </table>
                </div>
              )}

              {activeTab === "brands" && (
                <div className="yr-wrap">
                  <table className="yr-table yr-table--sticky yr-table--brands">
                    <thead>
                      <tr>
                        <th className="yr-title" colSpan={14}>
                          Brand current sales — {fyLabel} — {branchLabel}
                        </th>
                      </tr>
                      <tr>
                        <th>Brand</th>
                        {FY_MONTH_ORDER_SHORT.map((m) => (
                          <th key={m}>{m}</th>
                        ))}
                        <th>YTD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandMatrix.map(({ brand, monthly, ytd }) => (
                        <tr key={brand}>
                          <td>{brand}</td>
                          {monthly.map((v, i) => (
                            <td key={i}>{fmt(v)}</td>
                          ))}
                          <td className="yr-num-strong">{fmt(ytd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "charts" && (
                <div className="yr-charts">
                  <div className="yr-chart-card">
                    <h3 className="yr-chart-title">Total sale vs purchase (monthly)</h3>
                    <div className="yr-chart-inner">
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={chartTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Number(v).toLocaleString("en-IN")} />
                          <Tooltip formatter={(v) => fmt(v)} />
                          <Legend />
                          <Line type="monotone" dataKey="totalSale" name="Total sale" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="totalPurchase" name="Total purchase" stroke="#64748b" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="yr-chart-card">
                    <h3 className="yr-chart-title">Cash vs credit vs other (monthly)</h3>
                    <div className="yr-chart-inner">
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={chartTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Number(v).toLocaleString("en-IN")} />
                          <Tooltip formatter={(v) => fmt(v)} />
                          <Legend />
                          <Bar dataKey="cash" name="Cash" stackId="a" fill="#22c55e" />
                          <Bar dataKey="credit" name="Credit" stackId="a" fill="#3b82f6" />
                          <Bar dataKey="other" name="Other" stackId="a" fill="#a855f7" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </section>

        <aside className="msr-sidepanel msr-no-print yr-side-advanced">
          <div className="msr-toolbar yr-toolbar-stack">
            <label className="msr-toolbar-label">
              Financial year{" "}
              <select
                className="msr-toolbar-select"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                title="April–March (e.g. 2025-26 is Apr 2025 through Mar 2026)"
              >
                {fySelectOptions.map(({ startYear, label }) => (
                  <option key={startYear} value={startYear}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <span className="text-xs text-slate-600 block -mt-1 mb-1">
              {fyLabel} · April–March
            </span>
            <label className="msr-toolbar-label">
              Branch{" "}
              <select
                className="msr-toolbar-select"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                {BRANCH_OPTIONS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="yr-check">
              <input
                type="checkbox"
                checked={useFastLoad}
                onChange={(e) => setUseFastLoad(e.target.checked)}
              />
              Fast load (4 mo. batches)
            </label>
            <label className="yr-check">
              <input
                type="checkbox"
                checked={comparePriorYear}
                onChange={(e) => setComparePriorYear(e.target.checked)}
              />
              Compare prior FY ({formatFinancialYearLabel(year - 1)})
            </label>
            {compareLoading ? (
              <span className="yr-mini-status">Loading {formatFinancialYearLabel(year - 1)}…</span>
            ) : null}
          </div>

          <div className="yr-col-toggles">
            <div className="yr-col-toggles-title">Grid columns</div>
            {GRID_COLUMNS.map((c) => (
              <label key={c.key} className="yr-check yr-check--sm">
                <input
                  type="checkbox"
                  checked={visibleCols[c.key]}
                  onChange={() => toggleCol(c.key)}
                />
                {c.label}
              </label>
            ))}
          </div>

          <div className="yr-actions">
            <button type="button" className="msr-print-btn" onClick={() => window.print()}>
              Print
            </button>
            <button type="button" className="yr-btn-secondary" onClick={exportGridCsv} disabled={!rows.length}>
              Export grid CSV
            </button>
            <button type="button" className="yr-btn-secondary" onClick={exportBrandCsv} disabled={!rows.length}>
              Export brands CSV
            </button>
            <button type="button" className="yr-btn-secondary" onClick={loadMain} disabled={loading}>
              Refresh
            </button>
          </div>
          <p className="text-xs text-slate-600 mt-2 px-1 leading-relaxed">
            Default: FY months load one-by-one with a progress bar. Fast load batches four months.
            Prior-FY comparison runs after the main financial year finishes.
          </p>
        </aside>
      </div>
    </>
  );
}
